import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Button,
  TextField,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Checkbox,
} from "@mui/material";
import {
  bulkUpdateCategoryRates,
  getAdminCategories,
  updateCategoryRates,
} from "../../services/jobService";
import { useResponseError } from "../../hooks/useResponseError";
import { ErrorBox } from "../../elements/ErrorBox";

type CategoryRate = {
  id: string;
  name: string;
  parentId: string | null;
  parentName: string | null;
  clientHourly: number | null;
  assistantHourly: number | null;
  hasQuestionnaire: boolean;
  updatedAt: string;
};

const AdminRatesList = () => {
  const [categories, setCategories] = useState<CategoryRate[]>([]);
  const [drafts, setDrafts] = useState<
    Record<string, { clientHourly: string; assistantHourly: string }>
  >({});
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [bulkClientHourly, setBulkClientHourly] = useState("");
  const [bulkAssistantHourly, setBulkAssistantHourly] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [savingId, setSavingId] = useState<string | null>(null);
  const [isBulkSaving, setIsBulkSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState("");
  const { responseError, setResponseError } = useResponseError(15000);

  const applyCategories = (items: CategoryRate[]) => {
    setCategories(items || []);
    const nextDrafts: Record<
      string,
      { clientHourly: string; assistantHourly: string }
    > = {};
    (items || []).forEach((item) => {
      nextDrafts[item.id] = {
        clientHourly:
          item.clientHourly != null ? String(item.clientHourly) : "",
        assistantHourly:
          item.assistantHourly != null ? String(item.assistantHourly) : "",
      };
    });
    setDrafts(nextDrafts);
  };

  const loadCategories = async () => {
    setIsLoading(true);
    try {
      const res = await getAdminCategories();
      applyCategories(res || []);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    loadCategories();
  }, []);

  const handleDraftChange = (
    id: string,
    field: "clientHourly" | "assistantHourly",
    value: string
  ) => {
    setDrafts((prev) => ({
      ...prev,
      [id]: {
        ...prev[id],
        [field]: value,
      },
    }));
  };

  const handleSaveRow = async (id: string) => {
    const draft = drafts[id];
    if (!draft) {
      return;
    }
    setSavingId(id);
    setSuccessMessage("");
    try {
      await updateCategoryRates({
        id,
        clientHourly:
          draft.clientHourly === "" ? undefined : Number(draft.clientHourly),
        assistantHourly:
          draft.assistantHourly === ""
            ? undefined
            : Number(draft.assistantHourly),
      });
      setSuccessMessage("Rates updated.");
      await loadCategories();
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setSavingId(null);
    }
  };

  const toggleSelected = (id: string) => {
    setSelectedIds((prev) =>
      prev.includes(id) ? prev.filter((item) => item !== id) : [...prev, id]
    );
  };

  const toggleAll = () => {
    if (selectedIds.length === categories.length) {
      setSelectedIds([]);
    } else {
      setSelectedIds(categories.map((c) => c.id));
    }
  };

  const handleBulkApply = async () => {
    if (!selectedIds.length) {
      setResponseError("Select at least one category.");
      return;
    }
    if (bulkClientHourly === "" && bulkAssistantHourly === "") {
      setResponseError("Enter a client or assistant hourly rate to apply.");
      return;
    }
    setIsBulkSaving(true);
    setSuccessMessage("");
    try {
      const res = await bulkUpdateCategoryRates({
        categoryIds: selectedIds,
        clientHourly:
          bulkClientHourly === "" ? undefined : Number(bulkClientHourly),
        assistantHourly:
          bulkAssistantHourly === ""
            ? undefined
            : Number(bulkAssistantHourly),
      });
      applyCategories(res || []);
      setSuccessMessage("Bulk rates applied.");
      setSelectedIds([]);
    } catch (error: unknown) {
      setResponseError((error as Error).message);
    } finally {
      setIsBulkSaving(false);
    }
  };

  return (
    <Box>
      <Typography variant="h1" component="h1" sx={{ mb: "32px" }}>
        Rates
      </Typography>
      {responseError && <ErrorBox sx={{ mb: 2 }}>{responseError}</ErrorBox>}
      {successMessage && (
        <Alert
          severity="success"
          sx={{ mb: 2 }}
          onClose={() => setSuccessMessage("")}
        >
          {successMessage}
        </Alert>
      )}

      <Paper
        elevation={0}
        sx={{
          border: "1px solid #DDDFDD",
          p: 2,
          mb: 3,
          display: "flex",
          flexWrap: "wrap",
          gap: 2,
          alignItems: "center",
        }}
      >
        <TextField
          label="Client hourly"
          type="number"
          size="small"
          value={bulkClientHourly}
          onChange={(e) => setBulkClientHourly(e.target.value)}
          sx={{ width: 160 }}
        />
        <TextField
          label="Assistant hourly"
          type="number"
          size="small"
          value={bulkAssistantHourly}
          onChange={(e) => setBulkAssistantHourly(e.target.value)}
          sx={{ width: 160 }}
        />
        <Button
          variant="contained"
          onClick={handleBulkApply}
          disabled={isBulkSaving || !selectedIds.length}
        >
          {isBulkSaving
            ? "Applying..."
            : `Apply to selected (${selectedIds.length})`}
        </Button>
      </Paper>

      {isLoading ? (
        <Box
          sx={{
            mt: "40px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "300px",
          }}
        >
          <CircularProgress />
        </Box>
      ) : (
        <TableContainer
          component={Paper}
          elevation={0}
          sx={{ border: "1px solid #DDDFDD" }}
        >
          <Table>
            <TableHead>
              <TableRow>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={
                      categories.length > 0 &&
                      selectedIds.length === categories.length
                    }
                    indeterminate={
                      selectedIds.length > 0 &&
                      selectedIds.length < categories.length
                    }
                    onChange={toggleAll}
                  />
                </TableCell>
                <TableCell>Category</TableCell>
                <TableCell>Parent</TableCell>
                <TableCell>Client hourly</TableCell>
                <TableCell>Assistant hourly</TableCell>
                <TableCell>Action</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {categories.map((category) => (
                <TableRow key={category.id} hover>
                  <TableCell padding="checkbox">
                    <Checkbox
                      checked={selectedIds.includes(category.id)}
                      onChange={() => toggleSelected(category.id)}
                    />
                  </TableCell>
                  <TableCell>{category.name}</TableCell>
                  <TableCell>{category.parentName || "—"}</TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={drafts[category.id]?.clientHourly ?? ""}
                      onChange={(e) =>
                        handleDraftChange(
                          category.id,
                          "clientHourly",
                          e.target.value
                        )
                      }
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <TextField
                      type="number"
                      size="small"
                      value={drafts[category.id]?.assistantHourly ?? ""}
                      onChange={(e) =>
                        handleDraftChange(
                          category.id,
                          "assistantHourly",
                          e.target.value
                        )
                      }
                      sx={{ width: 120 }}
                    />
                  </TableCell>
                  <TableCell>
                    <Button
                      variant="table"
                      onClick={() => handleSaveRow(category.id)}
                      disabled={savingId === category.id}
                    >
                      {savingId === category.id ? "Saving..." : "Save"}
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </Box>
  );
};

export default AdminRatesList;
